import { createContext, Dispatch, useReducer, PropsWithChildren, useContext } from "react";
import { ToastType, ToastAction } from "livemap-ui";

export interface ToastInfo {
  id: string;
  type: ToastType;
  title?: string;
  text: string;
  actions?: ToastAction[];
  duration?: number;
}

export interface RemoveToastAction {
  type: "REMOVE_TOAST";
  payload: string;
}

export interface AddToastAction {
  type: "ADD_TOAST";
  payload: ToastInfo;
}

export type ToastReducerAction = AddToastAction | RemoveToastAction;

export type ToastDispatch = Dispatch<ToastReducerAction>;

export type AddToastFunction = (toast: ToastInfo) => void;

const ToastContext = createContext<ToastInfo[]>([]);
let toastDispatch: ToastDispatch = () => void 0;

function toastReducer(toasts: ToastInfo[], action: ToastReducerAction) {
  switch (action.type) {
    case "ADD_TOAST":
      return [...toasts, action.payload];
    case "REMOVE_TOAST":
      return toasts.filter(toast => action.payload !== toast.id);
    default:
      return toasts;
  }
}

export function useToasts(): ToastInfo[] {
  return useContext(ToastContext);
}

export function useAddToast(): AddToastFunction {
  return ({ duration = 7500, ...toast }: ToastInfo) => {
    toastDispatch({ type: "REMOVE_TOAST", payload: toast.id });
    toastDispatch({ type: "ADD_TOAST", payload: { ...toast, duration } });

    setTimeout(
      () => toastDispatch({ type: "REMOVE_TOAST", payload: toast.id }),
      duration
    );
  };
}

export function ToastProvider({ children }: PropsWithChildren<{}>) {
  const [toasts, dispatch] = useReducer(toastReducer, []);
  toastDispatch = dispatch;

  return <ToastContext.Provider value={toasts}>{children}</ToastContext.Provider>;
}
